/**
 * Odds Format Utilities
 *
 * Convert between decimal and American odds formats
 */

export type OddsFormat = 'decimal' | 'american';

/**
 * Convert decimal odds to American odds
 * Decimal > 2.0 becomes positive American (+)
 * Decimal < 2.0 becomes negative American (-)
 */
export function decimalToAmerican(decimal: number): number {
  if (decimal >= 2.0) {
    // Favorite: positive odds
    return Math.round((decimal - 1) * 100);
  } else {
    // Underdog: negative odds
    return Math.round(-100 / (decimal - 1));
  }
}

/**
 * Convert American odds to decimal odds
 * Positive American odds (underdog)
 * Negative American odds (favorite)
 */
export function americanToDecimal(american: number): number {
  if (american > 0) {
    return (american / 100) + 1;
  } else {
    return (100 / Math.abs(american)) + 1;
  }
}

/**
 * Format odds for display based on selected format
 */
export function formatOdds(odds: number, format: OddsFormat, inputFormat: OddsFormat = 'decimal'): string {
  let decimalOdds = odds;

  // Convert to decimal if input is American
  if (inputFormat === 'american') {
    decimalOdds = americanToDecimal(odds);
  }

  // Format based on desired output
  if (format === 'decimal') {
    return decimalOdds.toFixed(2);
  } else {
    const american = decimalToAmerican(decimalOdds);
    return american > 0 ? `+${american}` : `${american}`;
  }
}

/**
 * Get the actual odds value in the requested format
 */
export function getOddsValue(odds: number, format: OddsFormat, inputFormat: OddsFormat = 'decimal'): number {
  let decimalOdds = odds;

  // Convert to decimal if input is American
  if (inputFormat === 'american') {
    decimalOdds = americanToDecimal(odds);
  }

  // Return in requested format
  if (format === 'decimal') {
    return decimalOdds;
  } else {
    return decimalToAmerican(decimalOdds);
  }
}
